import React from 'react';
import {Box} from '@mui/material';
import {NAVY, TEXTE_SECONDAIRE} from '../../tokens.js';
import {constatDeclasses, constatDerives, constatEffetLavage, nbFr, nomMois, pctFr} from '../../selectors/analyses.js';
import Courbe from '../Courbe.js';
import CarteAnalyse from './CarteAnalyse.js';
import PetitTableau from './PetitTableau.js';

// Lot 10 : les trois cartes « données simulées » (S10a-c). Chacune reçoit le résultat de sa fonction de selectors/analyses.js.

const GRIS = '#6B7480';
const tauxOuTiret = (t) => (t === null ? '—' : pctFr(t));
const Legende = ({children}) => <Box component="p" sx={{m: '0 0 4px', fontSize: 12, color: TEXTE_SECONDAIRE}}>{children}</Box>;

/** Carte 3 : effet du lavage sur l'usage. `resultat` = effetLavage(index). */
export function CarteEffetLavage({resultat, signal, lienCarte}) {
    return (
        <CarteAnalyse id="analyse-effet-lavage" titre="Effet du lavage sur l'usage" source="simule" constat={constatEffetLavage(resultat)} signal={signal} lienCarte={lienCarte}>
            <Legende>Écart de remplissage par rapport à avant, par semaine autour du 2e lavage impossible (S0 = 1re collecte après)</Legende>
            <Courbe points={resultat.courbe} couleur={GRIS} format={(y) => `${y > 0 ? '+' : ''}${pctFr(y, 0)}`}/>
            <Box sx={{display: 'flex', gap: '24px', mt: '8px', fontSize: 12, color: TEXTE_SECONDAIRE}}>
                <span>Avant : <b style={{color: NAVY}}>{tauxOuTiret(resultat.avant)}</b></span>
                <span>Pendant : <b style={{color: NAVY}}>{tauxOuTiret(resultat.pendant)}</b></span>
                <span>Après le lavage : <b style={{color: NAVY}}>{tauxOuTiret(resultat.apres)}</b> ({resultat.nRebond} bornes)</span>
            </Box>
        </CarteAnalyse>
    );
}

const pentePts = (p) => `${p < 0 ? '−' : '+'}${nbFr(Math.abs(p) * 100)} pt/mois`;

/** Carte 4 : quartiers en dérive. `resultat` = derives(index). */
export function CarteDerive({resultat, signal, lienCarte}) {
    const tete = resultat.circuits[0];
    const lignes = resultat.circuits.slice(0, 5).map((c) => ({cle: c.patrimoineId, circuit: c.libelle, pente: pentePts(c.pente)}));
    return (
        <CarteAnalyse id="analyse-derive" titre="Quartiers en dérive" source="simule" constat={constatDerives(resultat)} signal={signal} lienCarte={lienCarte}>
            {tete && (
                <>
                    <Legende>{`Remplissage moyen mensuel, ${tete.libelle} (${nomMois(resultat.debut)} à ${nomMois(resultat.fin)})`}</Legende>
                    <Courbe points={tete.serie} couleur={GRIS} format={(y) => pctFr(y, 1)}/>
                    <Box sx={{mt: '8px'}}>
                        <PetitTableau
                            colonnes={[{cle: 'circuit', libelle: 'Circuit'}, {cle: 'pente', libelle: 'Pente', droite: true}]}
                            lignes={lignes}
                            legende="Les 5 circuits dont le remplissage baisse le plus"
                        />
                    </Box>
                </>
            )}
        </CarteAnalyse>
    );
}

/** Carte 5 : déclassés, où et sur quelles bornes. `resultat` = declassesProfil(index). */
export function CarteDeclasses({resultat, signal, lienCarte}) {
    const communes = resultat.communes.map((c) => ({cle: c.cle, a: c.libelle, collectes: c.collectes, declasses: c.declasses, taux: tauxOuTiret(c.taux)}));
    const ages = resultat.ages.map((a) => ({cle: a.tranche, a: a.tranche, collectes: a.collectes, declasses: a.declasses, taux: tauxOuTiret(a.taux)}));
    const cols = (libelle) => [{cle: 'a', libelle}, {cle: 'collectes', libelle: 'Collectes', droite: true}, {cle: 'declasses', libelle: 'Déclassés', droite: true}, {cle: 'taux', libelle: 'Taux', droite: true}];
    return (
        <CarteAnalyse id="analyse-declasses" titre="Déclassés : où et sur quelles bornes" source="simule" constat={constatDeclasses(resultat)} signal={signal} lienCarte={lienCarte} pleineLargeur>
            {resultat.global.declasses > 0 && (
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '24px', alignItems: 'start'}}>
                    <PetitTableau colonnes={cols('Commune (5 premières)')} lignes={communes} legende="Taux de déclassement par commune"/>
                    <PetitTableau colonnes={cols('Âge de la borne')} lignes={ages} legende="Taux de déclassement par âge de la borne"/>
                </Box>
            )}
        </CarteAnalyse>
    );
}
